import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import hrService from '../../../services/hrService';
import { useAuth } from '../../../contexts/AuthContext';
import { formatEasternDateOnly } from '../../../utils/timezone';

const LeaveHistory = () => {
  const { user } = useAuth();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLeaveHistory();
  }, [user]);

  const loadLeaveHistory = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError('');
      
      // FIXED: Use the correct hrService method that actually exists
      const data = await hrService?.getLeaveRequests();
      setLeaveRequests(data || []);
    } catch (err) {
      console.error('Error loading leave history:', err);
      setError(err?.message || 'Failed to load leave history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'text-success bg-success/10';
      case 'rejected':
        return 'text-error bg-error/10';
      case 'pending':
        return 'text-warning bg-warning/10';
      default:
        return 'text-muted-foreground bg-muted/10';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return 'CheckCircle';
      case 'rejected':
        return 'XCircle';
      case 'pending':
        return 'Clock';
      default:
        return 'AlertCircle';
    }
  };

  const formatDate = (dateString) => {
    return formatEasternDateOnly(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <span className="text-foreground">Loading leave history...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center py-8">
          <Icon name="AlertCircle" size={48} color="var(--color-error)" className="mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Error Loading History</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button
            onClick={loadLeaveHistory}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-semibold text-foreground">Leave History</h2>
        <p className="text-muted-foreground mt-1">View all your leave requests and their status</p>
      </div>
      <div className="p-6">
        {leaveRequests?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Calendar" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Leave Requests Yet</h3>
            <p className="text-muted-foreground">
              You haven't submitted any leave requests. Use the "Apply Leave" tab to submit your first request.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {leaveRequests?.map((request) => (
              <div
                key={request?.id}
                className="border border-border rounded-lg p-4 hover:bg-muted/20 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request?.leaveStatus)}`}>
                      <div className="flex items-center space-x-1">
                        <Icon name={getStatusIcon(request?.leaveStatus)} size={12} />
                        <span className="capitalize">{request?.leaveStatus}</span>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Applied on {formatDate(request?.createdAt)}
                    </div>
                  </div>
                  <div className="text-sm font-medium text-foreground">
                    {request?.daysRequested || 1} day{(request?.daysRequested || 1) !== 1 ? 's' : ''}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Leave Type</label>
                    <p className="text-sm text-foreground capitalize">{request?.leaveType?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Start Date</label>
                    <p className="text-sm text-foreground">{formatDate(request?.startDate)}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">End Date</label>
                    <p className="text-sm text-foreground">{formatDate(request?.endDate)}</p>
                  </div>
                </div>

                {request?.reason && (
                  <div className="mb-3">
                    <label className="text-xs font-medium text-muted-foreground">Reason</label>
                    <p className="text-sm text-foreground">{request?.reason}</p>
                  </div>
                )}

                {request?.approvalComments && (
                  <div className="mb-3">
                    <label className="text-xs font-medium text-muted-foreground">Admin Comments</label>
                    <p className="text-sm text-foreground">{request?.approvalComments}</p>
                  </div>
                )}

                {request?.approvalDate && request?.approvedByProfile && (
                  <div className="text-xs text-muted-foreground">
                    {request?.leaveStatus === 'approved' ? 'Approved' : 'Reviewed'} by {request?.approvedByProfile?.fullName} on {formatDate(request?.approvalDate)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveHistory;